import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import maplibregl from 'maplibre-gl';
import { api } from '../lib/api';

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county: string;
  state?: string;
  tech_type: string;
  awarded_bsl_count: number;
  location_count: number;
  total_budget_usd: string | null;
};

const fmtMo = (m: any) => {
  const v = Number(m);
  if (!isFinite(v) || v <= 0) return '—';
  if (v < 24) return `${v.toFixed(1)} mo`;
  return `${(v / 12).toFixed(1)} yr`;
};
const fmtUSD = (n: any) => {
  const v = Number(n);
  if (!isFinite(v) || v === 0) return '$0';
  if (Math.abs(v) >= 1_000_000) return `${v < 0 ? '-' : ''}$${(Math.abs(v) / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `${v < 0 ? '-' : ''}$${(Math.abs(v) / 1_000).toFixed(0)}k`;
  return `${v < 0 ? '-' : ''}$${Math.round(Math.abs(v)).toLocaleString()}`;
};


// Per-value localStorage persistence. User pins a slider value via the
// 📌 icon next to its label; that value becomes the initial state on
// the next page load. Removing the pin clears the saved value.
const LS_PREFIX = 'fb.bc.';
function loadDefault<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    return raw !== null ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}
function savePinned(key: string, value: any) {
  try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(value)); } catch {}
}
function clearPinned(key: string) {
  try { localStorage.removeItem(LS_PREFIX + key); } catch {}
}
function isPinned(key: string): boolean {
  try { return localStorage.getItem(LS_PREFIX + key) !== null; } catch { return false; }
}

import { useState as _useStateForPin } from 'react';
function PinToggle({ pinKey, value }: { pinKey: string; value: any }) {
  const [pinned, setPinned] = _useStateForPin(() => isPinned(pinKey));
  const toggle = (e: any) => {
    e.preventDefault(); e.stopPropagation();
    if (pinned) { clearPinned(pinKey); setPinned(false); }
    else { savePinned(pinKey, value); setPinned(true); }
  };
  // Keep storage in sync when value changes while pinned
  if (pinned) {
    try {
      const stored = localStorage.getItem(LS_PREFIX + pinKey);
      if (stored !== JSON.stringify(value)) savePinned(pinKey, value);
    } catch {}
  }
  return (
    <span
      onClick={toggle}
      role="button"
      tabIndex={0}
      title={pinned ? 'Pinned — click to unpin' : 'Save current value as default'}
      style={{
        cursor: 'pointer', fontSize: 11, marginLeft: 6,
        opacity: pinned ? 1 : 0.35,
        userSelect: 'none',
        color: pinned ? '#10b981' : '#64748b',
        fontWeight: pinned ? 700 : 400,
      }}
    >
      {pinned ? '📌 saved' : '📌 save'}
    </span>
  );
}

// County name -> US state abbreviation (covers Vistabeam's 3 states)
const COUNTY_STATE: Record<string, string> = {
  Albany: 'WY', Carbon: 'WY', Converse: 'WY', Laramie: 'WY', Natrona: 'WY',
  Platte: 'WY', Sheridan: 'WY', Sweetwater: 'WY', Fremont: 'WY', Park: 'WY',
  Johnson: 'WY', Campbell: 'WY',
  Larimer: 'CO', Weld: 'CO', Adams: 'CO', Arapahoe: 'CO', Boulder: 'CO',
  Denver: 'CO', Douglas: 'CO', Jefferson: 'CO', 'El Paso': 'CO', Pueblo: 'CO',
  Mesa: 'CO', Garfield: 'CO',
  Kimball: 'NE', Keith: 'NE', Cheyenne: 'NE', Banner: 'NE',
  // Arkansas counties (Aristotle / CHR HLD scope)
  Union: 'AR', Saline: 'AR', Lincoln: 'AR', Lonoke: 'AR', White: 'AR',
  Chicot: 'AR', Dallas: 'AR', Drew: 'AR', Phillips: 'AR', Cleveland: 'AR',
  Ashley: 'AR', 'Hot Spring': 'AR', Ouachita: 'AR', Pulaski: 'AR',
};
function stateOf(p: { county?: string; state?: string }): string {
  // Prefer explicit state from the bead_award join; fall back to county map.
  if (p.state) return p.state;
  return COUNTY_STATE[p.county ?? ''] ?? 'Other';
}

// Project colors for map overlay
const PALETTE = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#a855f7', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#8b5cf6', '#14b8a6'];

export default function BusinessCasePage() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Selection + econ assumptions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [arpu, setArpu] = useState(() => loadDefault<number>('arpu', 75));
  const [takeRateUnserved, setTakeRateUnserved] = useState(() => loadDefault<number>('takeRateUnserved', 0.60));
  const [takeRateServed,   setTakeRateServed]   = useState(() => loadDefault<number>('takeRateServed',   0.25));
  const [margin, setMargin] = useState(() => loadDefault<number>('margin', 0.60));
  const [horizon, setHorizon] = useState(() => loadDefault<number>('horizon', 60));
  const [aerialMixPct, setAerialMixPct] = useState<number>(() => loadDefault<number>('aerialMix', 50));

  // Bolt-on
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());
  const [boltOnEnabled, setBoltOnEnabled] = useState(false);
  const [bufferFt, setBufferFt] = useState(() => loadDefault<number>('bufferFt', 1500));
  // Distance-tiered bolt-on pricing. Each tier covers an inclusive upper
  // bound (ft from the nearest awarded BSL); default rates reflect drop
  // install + ONT + MST share scaling with drop length.
  const [boltTiers, setBoltTiers] = useState(() => loadDefault('boltTiers', [
    { label: '0–200 ft',     ub: 200,  rate: 100 },
    { label: '200–500 ft',   ub: 500,  rate: 350 },
    { label: '500–750 ft',   ub: 750,  rate: 600 },
    { label: '750–1000 ft',  ub: 1000, rate: 900 },
    { label: '1000–1500 ft', ub: 1500, rate: 1300 },
    { label: '1500+ ft',          ub: Infinity, rate: 1800 },
  ]));

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Project-id → color map (stable)
  const colorMap = useMemo(() => {
    const m: Record<string, string> = {};
    projects.forEach((p, i) => { m[p.id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [projects]);

  // Init map once
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const m = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          sat: {
            type: 'raster',
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
            attribution: '© Esri',
          },
        },
        layers: [{ id: 'sat', type: 'raster', source: 'sat' }],
      } as any,
      center: [-106.5, 42.8],
      zoom: 6.5,
    });
    m.addControl(new maplibregl.NavigationControl());
    m.on('load', () => { setMapReady(true); });
    mapRef.current = m;
    return () => { m.remove(); mapRef.current = null; };
  }, []);

  // Fetch per-project BSLs for each selected project in parallel
  const selectedArr = [...selectedIds];
  const bslQueries = useQuery({
    queryKey: ['bead-locations-multi', selectedArr.sort().join(',')],
    queryFn: async () => {
      const results = await Promise.all(selectedArr.map((id) =>
        api.get(`/bead/projects/${id}/locations`).then((fc: any) => ({ id, fc }))
      ));
      return results;
    },
    enabled: selectedArr.length > 0,
  });

  // Per-project budget totals
  const { data: budgetTotals = {} } = useQuery<Record<string, { total: string; federal: string; match: string }>>({
    queryKey: ['bead-budget-totals', selectedArr.sort().join(',')],
    queryFn: async () => {
      const entries = await Promise.all(selectedArr.map(async (id) => {
        const b = await api.get(`/bead/projects/${id}/budget`);
        return [id, b.totals] as const;
      }));
      return Object.fromEntries(entries);
    },
    enabled: selectedArr.length > 0,
  });

  // Bolt-on fetches (one per selected project when enabled)
  const { data: boltOns = {}, isLoading: boltOnLoading } = useQuery<Record<string, { bolton_count: number; features: any }>>({
    queryKey: ['bolton', selectedArr.sort().join(','), bufferFt, boltOnEnabled],
    queryFn: async () => {
      if (!boltOnEnabled) return {};
      const entries = await Promise.all(selectedArr.map(async (id) => {
        const r = await api.get(`/bead/projects/${id}/bolt-on?buffer_ft=${bufferFt}`);
        return [id, r] as const;
      }));
      return Object.fromEntries(entries);
    },
    enabled: boltOnEnabled && selectedArr.length > 0,
  });

  // Render BSL + bolt-on overlays on the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Clean up existing project layers
    map.getStyle().layers
      .filter((l) => l.id.startsWith('bsl-') || l.id.startsWith('bolton-'))
      .forEach((l) => { if (map.getLayer(l.id)) map.removeLayer(l.id); });
    Object.keys(map.getStyle().sources ?? {})
      .filter((k) => k.startsWith('bsl-') || k.startsWith('bolton-'))
      .forEach((s) => { try { map.removeSource(s); } catch {} });

    const allBounds = new maplibregl.LngLatBounds();

    // Awarded BSLs per selected project
    if (bslQueries.data) {
      for (const { id, fc } of bslQueries.data) {
        if (!fc?.features?.length) continue;
        const color = colorMap[id] ?? '#f59e0b';
        const srcId = `bsl-${id}`;
        map.addSource(srcId, { type: 'geojson', data: fc } as any);
        map.addLayer({
          id: `bsl-dot-${id}`, type: 'circle', source: srcId,
          paint: {
            'circle-radius': 4,
            'circle-color': color,
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 1.2,
          },
        });
        for (const f of fc.features) {
          const c = f.geometry?.coordinates;
          if (Array.isArray(c)) allBounds.extend(c as [number, number]);
        }
      }
    }

    // Bolt-on overlays (grey)
    if (boltOnEnabled) {
      for (const [id, data] of Object.entries(boltOns)) {
        if (!data?.features?.features?.length) continue;
        const srcId = `bolton-${id}`;
        map.addSource(srcId, { type: 'geojson', data: data.features } as any);
        map.addLayer({
          id: `bolton-dot-${id}`, type: 'circle', source: srcId,
          paint: {
            'circle-radius': 3,
            'circle-color': '#94a3b8',
            'circle-stroke-color': '#475569',
            'circle-stroke-width': 0.8,
            'circle-opacity': 0.75,
          },
        });
      }
    }

    if (!allBounds.isEmpty() && selectedArr.length > 0) {
      try { map.fitBounds(allBounds, { padding: 80, duration: 500, maxZoom: 12 }); } catch {}
    }
  }, [bslQueries.data, boltOns, boltOnEnabled, mapReady, colorMap]);

  // Plant economics (singleton) — drives ft_per_passing fallback for
  // route-length estimation and baseline_take_rate for MRR scaling.
  const { data: economics } = useQuery<any>({
    queryKey: ['plant-economics'],
    queryFn: () => api.get('/settings/economics').catch(() => null),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  // Per-project soil profile (parallel) — used to feed soil-aware
  // labor delta into the displayed capex/payback math.
  const soilQueries = useQueries({
    queries: selectedArr.map((id) => ({
      queryKey: ['soil-profile-project', id],
      queryFn: () => api.get(`/geohub-data/soil-profile?bead_project_id=${id}`),
      enabled: !!id,
      staleTime: 60 * 60 * 1000,
      retry: false,
    })),
  });
  const soilByProject = useMemo(() => {
    const m: Record<string, { blendedSoilPerFt: number; catalogPerFt: number; aerialPerFt: number | null; routeM: number | null }> = {};
    selectedArr.forEach((id, i) => {
      const d = soilQueries[i]?.data as any;
      if (!d || !d.construction_method_breakdown) return;
      m[id] = {
        blendedSoilPerFt: Number(d.blended_labor_per_ft ?? 0),
        catalogPerFt:     Number(d.catalog_default_per_ft ?? 0),
        aerialPerFt:      d.aerial_per_ft != null ? Number(d.aerial_per_ft) : null,
        routeM:           d.route_length_m != null ? Number(d.route_length_m) : null,
      };
    });
    return m;
  // soilQueries is recreated each render but its data references are stable
  // until the network resolves — depend on the data list explicitly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArr.join(','), JSON.stringify(soilQueries.map(q => q.data?.blended_labor_per_ft ?? null))]);

  // Business case math per selected project
  const businessCases = useMemo(() => {
    return selectedArr.map((id) => {
      const p = projects.find((x) => x.id === id);
      if (!p) return null;
      const b = (budgetTotals as any)[id] ?? { total: 0, federal: 0, match: 0 };
      const budgetCapex  = Number(b.total ?? 0);
      const federalShare = Number(b.federal ?? 0);
      const matchShare   = Number(b.match ?? 0);
      // Soil-aware labor delta: at current aerial mix, how much do per-ft
      // labor rates differ from catalog × route length? Anything > 0 is
      // overrun risk Vistabeam absorbs (federal grant is fixed at award).
      const soil = soilByProject[id];
      let soilLaborDelta = 0;
      let soilNote: string | null = null;
      if (soil && soil.blendedSoilPerFt > 0) {
        const aerialFrac = Math.max(0, Math.min(1, aerialMixPct / 100));
        const buriedFrac = 1 - aerialFrac;
        const aerialRate = soil.aerialPerFt;  // from catalog/settings — no hardcoded default
        if (aerialRate == null) { soilLaborDelta = 0; soilNote = 'aerial rate missing in cost catalog'; }
        const blended = aerialFrac * (aerialRate ?? 0) + buriedFrac * soil.blendedSoilPerFt;
        const catalog = aerialFrac * (aerialRate ?? 0) + buriedFrac * soil.catalogPerFt;
        // Use real route length when endpoint returned it (territory plan
        // path); otherwise estimate from passings × ft_per_passing
        // (configurable in Settings → Economics; default 250 for rural FTTH).
        const ftPerPassing = economics?.ft_per_passing ?? 250;
        const routeFt = soil.routeM && soil.routeM > 0
          ? soil.routeM / 0.3048
          : p.awarded_bsl_count * ftPerPassing;
        const routeBasis = soil.routeM && soil.routeM > 0 ? 'route' : 'estimated';
        soilLaborDelta = (blended - catalog) * routeFt;
        if (Math.abs(soilLaborDelta) > 1) {
          const miStr = (routeFt / 5280).toFixed(1);
          soilNote = `${soilLaborDelta >= 0 ? 'overrun' : 'savings'} vs catalog @ ${aerialMixPct}% aerial · ${routeBasis} ${miStr} mi × ${(blended - catalog) >= 0 ? '+' : ''}$${(blended - catalog).toFixed(2)}/ft`;
        }
      }
      const totalCapex     = budgetCapex + soilLaborDelta;          // engineered estimate
      const privateCapital = matchShare + Math.max(0, soilLaborDelta);  // overrun → private; savings stay with match
      const awardedPassings = p.awarded_bsl_count;
      // Awarded BSL count is the BEAD-eligible (unserved) population.
      const mrr = awardedPassings * takeRateUnserved * arpu;
      const monthlyMargin = mrr * margin;
      const beadOnlyPayback = monthlyMargin > 0 ? privateCapital / monthlyMargin : Infinity;
      const horizonProfit = monthlyMargin * horizon - privateCapital;
      // Bolt-on additions
      const boltData = (boltOns as any)[id];
      const boltOnCount = boltData?.bolton_count ?? 0;
      // Bolt-on count is non-BEAD nearby — i.e. already-served passings the
      // BEAD-funded fiber happens to pass. Lower take rate (incumbent friction).
      const boltOnMrr = boltOnCount * takeRateServed * arpu;
      // Distance-tiered capex: bin each bolt-on building by distance to
      // the nearest awarded BSL, multiply by the tier's $/passing rate.
      const awardedPts: Array<[number, number]> = boltData?.awarded_points ?? [];
      const boltFeats: any[] = boltData?.features?.features ?? [];
      // For TX projects, the backend pre-buckets bolt-on BSLs into our 6 tiers
      // using H3 ring distance (no exact lat/lng available). When tier_counts
      // is present, use it directly; otherwise fall back to per-feature distance
      // bucketing (works for WY/CO/NE which have FF-side bead_location with geom).
      const presetTiers: number[] | null = Array.isArray(boltData?.tier_counts) ? boltData.tier_counts : null;
      let tierCounts: number[];
      if (presetTiers && presetTiers.length === boltTiers.length) {
        tierCounts = presetTiers.slice();
      } else {
        tierCounts = new Array(boltTiers.length).fill(0);
        // Quick squared-Euclidean in degrees · 110_540 m/deg ~ planar OK at lat<60
        const FT_PER_DEG_LAT = 364320;
        for (const f of boltFeats) {
          const c = f.geometry?.coordinates;
          if (!Array.isArray(c) || awardedPts.length === 0) continue;
          const lat0 = (c[1] as number);
          const cosLat = Math.cos(lat0 * Math.PI / 180);
          let minD2 = Infinity;
          for (const ap of awardedPts) {
            const dx = ((c[0] as number) - ap[0]) * cosLat;
            const dy = (c[1] as number) - ap[1];
            const d2 = dx*dx + dy*dy;
            if (d2 < minD2) minD2 = d2;
          }
          const dFt = Math.sqrt(minD2) * FT_PER_DEG_LAT;
          for (let i = 0; i < boltTiers.length; i++) {
            if (dFt <= boltTiers[i].ub) { tierCounts[i]++; break; }
          }
        }
      }
      const boltOnCapex = boltTiers.reduce((acc, t, i) => acc + (tierCounts[i] * t.rate), 0);
      const boltOnMonthlyMargin = boltOnMrr * margin;
      const boltOnPayback = boltOnMonthlyMargin > 0 ? boltOnCapex / boltOnMonthlyMargin : Infinity;
      // Combined economics: when bolt-on is on, the deal includes both
      // the BEAD-awarded BSLs AND the ride-along homes that the trunk passes.
      // Private capital out-of-pocket = match + bolt-on capex; MRR = sum.
      const combinedPrivateCapital = privateCapital + (boltOnEnabled ? boltOnCapex : 0);
      const combinedMrr = mrr + (boltOnEnabled ? boltOnMrr : 0);
      const combinedMonthlyMargin = combinedMrr * margin;
      const combinedPayback = combinedMonthlyMargin > 0 ? combinedPrivateCapital / combinedMonthlyMargin : Infinity;
      // Headline paybackMonths reflects whichever scope is active.
      const paybackMonths = boltOnEnabled ? combinedPayback : beadOnlyPayback;
      // Verdict on whichever scope is active.
      let verdict: 'BUILD'|'MARGINAL'|'WALK AWAY' = 'WALK AWAY';
      if (paybackMonths <= 120) verdict = 'BUILD';          // <=10 yr
      else if (paybackMonths <= 180) verdict = 'MARGINAL';  // 10-15 yr

      return {
        id, code: p.project_code, name: p.project_name,
        awardedPassings, totalCapex, federalShare, matchShare, privateCapital,
        mrr, monthlyMargin, paybackMonths, horizonProfit, verdict,
        boltOnEnabled, boltOnCount, boltOnMrr, boltOnCapex, boltOnPayback,
        beadOnlyPayback, combinedPrivateCapital, combinedMrr, combinedPayback,
        tierCounts: [...tierCounts],
      };
    }).filter(Boolean) as any[];
  }, [selectedArr, projects, budgetTotals, arpu, takeRateUnserved, takeRateServed, margin, horizon, boltOns, boltOnEnabled, boltTiers, economics]);

  // Portfolio totals
  const port = businessCases.reduce((acc, bc) => ({
    passings:       acc.passings + bc.awardedPassings,
    capex:          acc.capex    + bc.totalCapex,
    federal:        acc.federal  + bc.federalShare,
    match:          acc.match    + bc.matchShare,
    mrr:            acc.mrr      + bc.mrr,
    bolt_passings:  acc.bolt_passings + (boltOnEnabled ? bc.boltOnCount : 0),
    bolt_capex:     acc.bolt_capex + (boltOnEnabled ? bc.boltOnCapex : 0),
    bolt_mrr:       acc.bolt_mrr + (boltOnEnabled ? bc.boltOnMrr : 0),
  }), { passings: 0, capex: 0, federal: 0, match: 0, mrr: 0, bolt_passings: 0, bolt_capex: 0, bolt_mrr: 0 });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr 360px', height: 'calc(100vh - 48px)', gap: 0 }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {/* LEFT: project list + econ assumptions */}
      <div style={{ padding: 14, overflowY: 'auto', borderRight: '1px solid var(--border)', background: '#f8fafc' }}>
        <h2 style={{ fontSize: 16, margin: '0 0 12px 0' }}>Project Economics</h2>

        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 6 }}>Projects</div>
        <div style={{ marginBottom: 6, display: 'flex', gap: 6 }}>
          <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setSelectedIds(new Set(projects.map((p) => p.id)))}>All</button>
          <button className="btn" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setSelectedIds(new Set())}>None</button>
        </div>
        {(() => {
          const byState: Record<string, Project[]> = {};
          for (const p of projects) {
            const st = stateOf(p);
            (byState[st] = byState[st] || []).push(p);
          }
          // Show every state that has projects, with our known states first then alphabetical
          const knownOrder = ['WY', 'CO', 'NE', 'TX', 'AR'];
          const allStates = Object.keys(byState);
          const stateOrder = [
            ...knownOrder.filter(s => byState[s]?.length),
            ...allStates.filter(s => !knownOrder.includes(s) && s !== 'Other').sort(),
            ...(byState['Other']?.length ? ['Other'] : [])
          ];
          return stateOrder.map((st) => {
            const ps = byState[st];
            const isExp = expandedStates.has(st);
            const stateBsls = ps.reduce((a, p) => a + Number(p.awarded_bsl_count || 0), 0);
            const allSelected = ps.every((p) => selectedIds.has(p.id));
            const noneSelected = ps.every((p) => !selectedIds.has(p.id));
            return (
              <div key={st} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 2px', fontSize: 11, fontWeight: 700, color: '#334155', cursor: 'pointer', userSelect: 'none', borderBottom: '1px solid #e2e8f0' }}
                  onClick={() => setExpandedStates((prev) => { const n = new Set(prev); if (n.has(st)) n.delete(st); else n.add(st); return n; })}>
                  <span style={{ width: 12, color: '#64748b' }}>{isExp ? '▾' : '▸'}</span>
                  <input type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) ps.forEach((p) => next.add(p.id));
                        else ps.forEach((p) => next.delete(p.id));
                        return next;
                      });
                    }} />
                  <span style={{ flex: 1 }}>{st}</span>
                  <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>{ps.length} · {stateBsls.toLocaleString()} BSLs</span>
                </div>
                {isExp && ps.map((p) => {
                  const sel = selectedIds.has(p.id);
                  return (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 2px 4px 24px', fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={sel}
                        onChange={(e) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(p.id); else next.delete(p.id);
                            return next;
                          });
                        }} />
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: sel ? colorMap[p.id] : '#cbd5e1' }} />
                      <span style={{ flex: 1, fontWeight: sel ? 600 : 400 }}>{p.project_code}</span>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{p.awarded_bsl_count}</span>
                    </label>
                  );
                })}
              </div>
            );
          });
        })()}

        <div style={{ marginTop: 20, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 6 }}>Assumptions</div>
        <div style={{ position: 'relative' }}>
          <Slider label={`ARPU $${arpu}/mo`}  value={arpu}        min={40}  max={200} step={5}   onChange={setArpu} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="arpu" value={arpu} /></span>
        </div>
        <div style={{ position: 'relative' }}>
          <Slider label={`Take rate · BEAD (unserved) ${(takeRateUnserved*100).toFixed(0)}%`} value={takeRateUnserved} min={0.1} max={0.9} step={0.05} onChange={setTakeRateUnserved} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="takeRateUnserved" value={takeRateUnserved} /></span>
        </div>
        <div style={{ position: 'relative' }}>
          <Slider label={`Take rate · already-served ${(takeRateServed*100).toFixed(0)}%`} value={takeRateServed} min={0.1} max={0.9} step={0.05} onChange={setTakeRateServed} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="takeRateServed" value={takeRateServed} /></span>
        </div>
        <div style={{ position: 'relative' }}>
          <Slider label={`Gross margin ${(margin*100).toFixed(0)}%`} value={margin} min={0.3} max={0.9} step={0.02} onChange={setMargin} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="margin" value={margin} /></span>
        </div>
        <div style={{ position: 'relative' }}>
          <Slider label={`Horizon ${horizon} mo`} value={horizon} min={24} max={120} step={12} onChange={setHorizon} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="horizon" value={horizon} /></span>
        </div>
        <div style={{ position: 'relative' }}>
          <Slider label={`Aerial mix ${aerialMixPct}% / ${100 - aerialMixPct}% buried`} value={aerialMixPct} min={0} max={100} step={5}
            onChange={(v: number) => { setAerialMixPct(v); savePinned('aerialMix', v); }} />
          <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="aerialMix" value={aerialMixPct} /></span>
        </div>

        <div style={{ marginTop: 20, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 6 }}>Bolt-on (non-BEAD nearby)</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
          <input type="checkbox" checked={boltOnEnabled} onChange={(e) => setBoltOnEnabled(e.target.checked)} />
          Include nearby passings
        </label>
        {boltOnEnabled && (
          <div style={{ position: 'relative' }}>
            <Slider label={`Buffer ${bufferFt} ft`} value={bufferFt} min={100} max={2000} step={50} onChange={setBufferFt} />
            <span style={{ position: 'absolute', top: 0, right: 0 }}><PinToggle pinKey="bufferFt" value={bufferFt} /></span>
          </div>
        )}
        {boltOnEnabled && (
          <div style={{ marginTop: 8, padding: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11 }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>$ per passing by distance to nearest awarded BSL</span>
              <PinToggle pinKey="boltTiers" value={boltTiers} />
            </div>
            {boltTiers.map((t, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                <span style={{ color: '#475569' }}>{t.label}</span>
                <input type="number" min={0} max={5000} step={25} value={t.rate}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setBoltTiers((prev) => prev.map((tt, ii) => ii === i ? { ...tt, rate: v } : tt));
                  }}
                  style={{ width: '100%', fontSize: 11, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 3 }} />
              </div>
            ))}
          </div>
        )}

      </div>

      {/* CENTER: map */}
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
        {boltOnEnabled && boltOnLoading && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: 'rgba(15, 23, 42, 0.92)', color: '#fff',
            padding: '16px 24px', borderRadius: 8,
            boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', gap: 14,
            fontSize: 14, fontWeight: 600,
            border: '1px solid #334155',
            zIndex: 999,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              border: '3px solid #334155',
              borderTopColor: '#10b981',
              animation: 'spin 0.8s linear infinite',
            }} />
            <div>
              Querying GeoHub for nearby passings…
              <div style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>
                {selectedArr.length} project{selectedArr.length === 1 ? '' : 's'} · {bufferFt} ft buffer
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: business case */}
      <div style={{ padding: 14, overflowY: 'auto', borderLeft: '1px solid var(--border)', background: '#fff' }}>
        {selectedArr.length === 0 ? (
          <div style={{ color: '#64748b' }}>
            <h3 style={{ fontSize: 14, marginTop: 0 }}>Select one or more awarded projects</h3>
            <p style={{ fontSize: 12 }}>The business case updates live. Toggle bolt-on to see what nearby non-BEAD passings you could grab with the trunk already paid for by the grant.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ fontSize: 15, margin: 0 }}>Portfolio ({businessCases.length} project{businessCases.length === 1 ? '' : 's'})</h3>
              <button onClick={() => {
                const params = new URLSearchParams({
                  projects: selectedArr.join(','),
                  arpu: String(arpu),
                  takeRateUnserved: String(takeRateUnserved),
                  takeRateServed:   String(takeRateServed),
                  margin: String(margin),
                  horizon: String(horizon),
                  boltOn: boltOnEnabled ? '1' : '0',
                  bufferFt: String(bufferFt),
                  tiers: boltTiers.map((t: any) => `${t.ub === Infinity ? 'inf' : t.ub}:${t.rate}`).join(','),
                });
                window.open(`/project-economics-report?${params.toString()}`, '_blank');
              }} style={{
                background: '#0f172a', color: '#fff', border: 0,
                padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
              }}>
                Generate Report
              </button>
            </div>
            <Scoreboard
              boltOnEnabled={boltOnEnabled}
              margin={margin}
              horizon={horizon}
              cols={{
                bead: {
                  passings: port.passings,
                  capex: port.capex,
                  federal: port.federal,
                  privateCapital: port.match,
                  mrr: port.mrr,
                },
                bolt: {
                  passings: port.bolt_passings,
                  capex: port.bolt_capex,
                  federal: 0,
                  privateCapital: port.bolt_capex,
                  mrr: port.bolt_mrr,
                },
                combined: {
                  passings: port.passings + port.bolt_passings,
                  capex: port.capex + port.bolt_capex,
                  federal: port.federal,
                  privateCapital: port.match + port.bolt_capex,
                  mrr: port.mrr + port.bolt_mrr,
                },
              }}
            />

            <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b' }}>Per project</h3>
            {businessCases.map((bc) => (
              <div key={bc.id} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: colorMap[bc.id], marginRight: 6 }} />
                      {bc.code}
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{bc.name} · {bc.awardedPassings} passings</div>
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 700,
                    background: bc.verdict === 'BUILD' ? '#dcfce7' : bc.verdict === 'MARGINAL' ? '#fef3c7' : '#fee2e2',
                    color:      bc.verdict === 'BUILD' ? '#047857' : bc.verdict === 'MARGINAL' ? '#92400e' : '#b91c1c',
                  }}>{bc.verdict}</span>
                </div>
                <Scoreboard
                  boltOnEnabled={boltOnEnabled}
                  margin={margin}
                  horizon={horizon}
                  compact
                  cols={{
                    bead: {
                      passings: bc.awardedPassings,
                      capex: bc.totalCapex,
                      federal: bc.federalShare,
                      privateCapital: bc.matchShare,
                      mrr: bc.mrr,
                    },
                    bolt: {
                      passings: bc.boltOnCount,
                      capex: bc.boltOnCapex,
                      federal: 0,
                      privateCapital: bc.boltOnCapex,
                      mrr: bc.boltOnMrr,
                    },
                    combined: {
                      passings: bc.awardedPassings + (boltOnEnabled ? bc.boltOnCount : 0),
                      capex: bc.totalCapex + (boltOnEnabled ? bc.boltOnCapex : 0),
                      federal: bc.federalShare,
                      privateCapital: bc.combinedPrivateCapital,
                      mrr: bc.combinedMrr,
                    },
                  }}
                />
                <ProjectSoilLine projectId={bc.id} aerialMixPct={aerialMixPct} />
                {Math.abs(bc.soilLaborDelta ?? 0) > 1 && (
                  <div style={{
                    marginTop: 4, padding: '4px 8px',
                    background: (bc.soilLaborDelta ?? 0) > 0 ? '#fef2f2' : '#ecfdf5',
                    border: '1px solid ' + ((bc.soilLaborDelta ?? 0) > 0 ? '#fecaca' : '#a7f3d0'),
                    borderRadius: 4, fontSize: 10, color: (bc.soilLaborDelta ?? 0) > 0 ? '#991b1b' : '#047857',
                    display: 'flex', justifyContent: 'space-between',
                  }}>
                    <span><strong>Capex includes soil-aware labor adj:</strong> {bc.soilNote}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {(bc.soilLaborDelta ?? 0) >= 0 ? '+' : '−'}{fmtUSD(Math.abs(bc.soilLaborDelta ?? 0))}
                    </span>
                  </div>
                )}
                {boltOnEnabled && bc.boltOnCount > 0 && (
                  <div style={{ marginTop: 6, padding: 6, background: '#fef3c7', borderRadius: 4, fontSize: 10 }}>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>
                      + {bc.boltOnCount.toLocaleString()} bolt-on · {fmtUSD(bc.boltOnCapex)} · {fmtMo(bc.boltOnPayback)} payback
                    </div>
                    {(bc.tierCounts ?? []).map((n: number, i: number) => n > 0 && (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#92400e', fontSize: 9.5 }}>
                        <span>{boltTiers[i].label}: {n.toLocaleString()} × ${boltTiers[i].rate}</span>
                        <span>{fmtUSD(n * boltTiers[i].rate)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange }: any) {
  return (
    <label style={{ display: 'block', fontSize: 11, marginBottom: 6 }}>
      <div style={{ color: '#64748b', marginBottom: 2 }}>{label}</div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: '100%' }} />
    </label>
  );
}


type ScoreCol = { passings: number; capex: number; federal: number; privateCapital: number; mrr: number };
function Scoreboard({ cols, margin, horizon, boltOnEnabled, compact }: { cols: { bead: ScoreCol; bolt: ScoreCol; combined: ScoreCol }; margin: number; horizon: number; boltOnEnabled: boolean; compact?: boolean }) {
  const showBolt = boltOnEnabled && cols.bolt.passings > 0;
  const calc = (c: ScoreCol) => {
    const gm = c.mrr * margin;
    const payback = gm > 0 ? c.privateCapital / gm : Infinity;
    const cashAtHorizon = gm * horizon - c.privateCapital; // negative = still recouping
    return { gm, payback, cashAtHorizon };
  };
  const beadCalc = calc(cols.bead);
  const boltCalc = calc(cols.bolt);
  const combCalc = calc(cols.combined);
  const bg = compact ? '#fff' : '#0b1220';
  const fg = compact ? '#0f172a' : '#fff';
  const muted = compact ? '#64748b' : '#94a3b8';
  const border = compact ? '1px solid #e2e8f0' : 'none';
  const headerBg = compact ? '#f8fafc' : '#1e293b';
  const num = (n: number) => isFinite(n) ? Math.round(n).toLocaleString() : '—';
  const cellPad = compact ? '4px 6px' : '6px 8px';
  const fontSize = compact ? 11 : 12;
  return (
    <div style={{ background: bg, color: fg, border, borderRadius: 6, padding: compact ? 6 : 10, marginBottom: compact ? 0 : 12, fontSize }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: cellPad, color: muted, fontWeight: 500, fontSize: fontSize - 1, textTransform: 'uppercase', letterSpacing: '.05em', background: headerBg }}></th>
            <th style={{ textAlign: 'right', padding: cellPad, fontWeight: 700, fontSize: fontSize - 1, background: headerBg }}>BEAD</th>
            {showBolt && <th style={{ textAlign: 'right', padding: cellPad, fontWeight: 700, fontSize: fontSize - 1, background: headerBg, color: '#f59e0b' }}>+ Bolt-on</th>}
            {showBolt && <th style={{ textAlign: 'right', padding: cellPad, fontWeight: 700, fontSize: fontSize - 1, background: headerBg, color: '#10b981' }}>= Combined</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: cellPad, color: muted }}>Passings</td>
            <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 600 }}>{num(cols.bead.passings)}</td>
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right' }}>+{num(cols.bolt.passings)}</td>}
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{num(cols.combined.passings)}</td>}
          </tr>
          <tr>
            <td style={{ padding: cellPad, color: muted }}>Capex</td>
            <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 600 }}>{fmtUSD(cols.bead.capex)}</td>
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right' }}>+{fmtUSD(cols.bolt.capex)}</td>}
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{fmtUSD(cols.combined.capex)}</td>}
          </tr>
          <tr>
            <td style={{ padding: '2px 6px 2px 18px', color: muted, fontSize: fontSize - 2 }}>Federal grant</td>
            <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>{fmtUSD(cols.bead.federal)}</td>
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>—</td>}
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>{fmtUSD(cols.combined.federal)}</td>}
          </tr>
          <tr>
            <td style={{ padding: '2px 6px 2px 18px', color: muted, fontSize: fontSize - 2 }}>Private capital (out-of-pocket)</td>
            <td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtUSD(cols.bead.privateCapital)}</td>
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right' }}>+{fmtUSD(cols.bolt.privateCapital)}</td>}
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{fmtUSD(cols.combined.privateCapital)}</td>}
          </tr>
          <tr>
            <td style={{ padding: cellPad, color: muted }}>MRR (take × ARPU)</td>
            <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 600 }}>{fmtUSD(cols.bead.mrr)}/mo</td>
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right' }}>+{fmtUSD(cols.bolt.mrr)}/mo</td>}
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{fmtUSD(cols.combined.mrr)}/mo</td>}
          </tr>
          <tr>
            <td style={{ padding: '2px 6px 2px 18px', color: muted, fontSize: fontSize - 2 }}>Margin @ {(margin*100).toFixed(0)}%</td>
            <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>{fmtUSD(beadCalc.gm)}/mo</td>
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>{fmtUSD(boltCalc.gm)}/mo</td>}
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', color: muted, fontSize: fontSize - 2 }}>{fmtUSD(combCalc.gm)}/mo</td>}
          </tr>
          <tr>
            <td style={{ padding: '2px 6px 2px 18px', color: muted, fontSize: fontSize - 2 }}>Cash @ {horizon} mo</td>
            <td style={{ padding: '2px 6px', textAlign: 'right', fontSize: fontSize - 1, color: beadCalc.cashAtHorizon >= 0 ? '#10b981' : '#f59e0b' }}>{beadCalc.cashAtHorizon >= 0 ? '+' : '−'}{fmtUSD(Math.abs(beadCalc.cashAtHorizon))}</td>
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', fontSize: fontSize - 1, color: boltCalc.cashAtHorizon >= 0 ? '#10b981' : '#f59e0b' }}>{boltCalc.cashAtHorizon >= 0 ? '+' : '−'}{fmtUSD(Math.abs(boltCalc.cashAtHorizon))}</td>}
            {showBolt && <td style={{ padding: '2px 6px', textAlign: 'right', fontSize: fontSize - 1, color: combCalc.cashAtHorizon >= 0 ? '#10b981' : '#f59e0b' }}>{combCalc.cashAtHorizon >= 0 ? '+' : '−'}{fmtUSD(Math.abs(combCalc.cashAtHorizon))}</td>}
          </tr>
          <tr style={{ borderTop: compact ? '1px solid #e2e8f0' : '1px solid #334' }}>
            <td style={{ padding: cellPad, color: muted, fontWeight: 600 }}>Payback (private)</td>
            <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 700 }}>{fmtMo(beadCalc.payback)}</td>
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right' }}>{fmtMo(boltCalc.payback)}</td>}
            {showBolt && <td style={{ padding: cellPad, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>{fmtMo(combCalc.payback)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: boolean; accent?: string }) {
  

return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: sub ? 2 : 6, paddingLeft: sub ? 8 : 0 }}>
      <span style={{ fontSize: sub ? 11 : 12, color: sub ? '#94a3b8' : '#cbd5e1' }}>{label}</span>
      <span style={{ fontSize: sub ? 11 : 13, fontWeight: sub ? 400 : 700, color: accent ?? '#fff' }}>{value}</span>
    </div>
  );
}

// --------------------------------------------------------------------
// ProjectSoilLine — compact one-row soil-aware labor summary per
// project. Pulls /geohub-data/soil-profile?bead_project_id=... and
// renders blended $/ft + adjustment + wetland flag.
// --------------------------------------------------------------------

function ProjectSoilLine({ projectId, aerialMixPct }: { projectId: string; aerialMixPct: number }) {
  const { data } = useQuery<any>({
    queryKey: ['soil-profile-project', projectId],
    queryFn: () => api.get(`/geohub-data/soil-profile?bead_project_id=${projectId}`),
    enabled: !!projectId,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  if (!data || !data.construction_method_breakdown) return null;

  const buriedSoil = Number(data.blended_labor_per_ft ?? 0);
  const buriedCat  = Number(data.catalog_default_per_ft ?? 0);
  const aerialRate = data.aerial_per_ft != null ? Number(data.aerial_per_ft) : null;
  const aerialFrac = Math.max(0, Math.min(1, aerialMixPct / 100));
  const buriedFrac = 1 - aerialFrac;
  const blended = aerialFrac * (aerialRate ?? 0) + buriedFrac * buriedSoil;
  const catalog = aerialFrac * (aerialRate ?? 0) + buriedFrac * buriedCat;
  const adj     = blended - catalog;
  const wetland = data.wetland_risk?.needs_404_review;
  const mb      = data.construction_method_breakdown;

  const blendStr = ['plow','trench','rock_saw','directional_bore']
    .map((k) => `${Math.round(mb[k]?.pct ?? 0)}%`)
    .join(' / ');

  const adjColor = adj > 0.5 ? '#ef4444' : adj < -0.5 ? '#10b981' : '#64748b';

  return (
    <div style={{
      marginTop: 6, padding: '6px 8px',
      background: '#f8fafc', border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 4, fontSize: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
    }}>
      <div>
        <span style={{ fontWeight: 700, color: '#0f172a' }}>Soil-aware labor:</span>
        {' '}<span style={{ fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>${blended.toFixed(2)}/ft</span>
        {' '}<span style={{ color: adjColor, fontWeight: 600 }}>
          ({adj >= 0 ? '+' : ''}${adj.toFixed(2)})
        </span>
        <div style={{ color: '#64748b', fontSize: 9, marginTop: 2 }}>
          {aerialMixPct}% aerial · buried mix Plow/Trench/Rock/Bore: {blendStr}
        </div>
      </div>
      {wetland && (
        <span style={{
          padding: '2px 6px', background: '#fef3c7', color: '#92400e',
          borderRadius: 3, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap',
        }}>⚠ §404 review</span>
      )}
    </div>
  );
}
